<template>
  <div class="navbar-content">
    <!-- 折叠按钮 -->
    <div class="hamburger" @click="toggleSidebar">
      <i :class="sidebarCollapsed ? 'el-icon-s-unfold' : 'el-icon-s-fold'"></i>
    </div>

    <!-- 面包屑 -->
    <div class="breadcrumb">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item v-for="item in levelList" :key="item.path">
          <span v-if="item.redirect === 'noRedirect' || item === levelList[levelList.length - 1]">
            {{ item.meta.title }}
          </span>
          <router-link v-else :to="item.redirect || item.path">
            {{ item.meta.title }}
          </router-link>
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <!-- 右侧功能 -->
    <div class="right-menu">
      <el-dropdown trigger="click" @command="handleCommand">
        <span class="el-dropdown-link">
          <i class="el-icon-user-solid"></i>
          <span class="username">{{ displayName }}</span>
          <i class="el-icon-arrow-down el-icon--right"></i>
        </span>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="changePassword">
            <i class="el-icon-edit-outline"></i> 修改密码
          </el-dropdown-item>
          <el-dropdown-item command="logout" divided>
            <i class="el-icon-switch-button"></i> 退出登录
          </el-dropdown-item>
        </el-dropdown-menu>

      <!-- 修改密码弹窗 -->
      <el-dialog title="修改密码" :visible.sync="passwordDialogVisible" width="420px" :close-on-click-modal="false" append-to-body>
        <el-form ref="passwordForm" :model="passwordForm" :rules="passwordRules" label-width="90px">
          <el-form-item label="旧密码" prop="oldPassword">
            <el-input v-model="passwordForm.oldPassword" type="password" placeholder="请输入当前密码" show-password clearable />
          </el-form-item>
          <el-form-item label="新密码" prop="newPassword">
            <el-input v-model="passwordForm.newPassword" type="password" placeholder="请输入新密码（至少6位）" show-password clearable />
          </el-form-item>
          <el-form-item label="确认密码" prop="confirmPassword">
            <el-input v-model="passwordForm.confirmPassword" type="password" placeholder="请再次输入新密码" show-password clearable />
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button @click="passwordDialogVisible = false">取 消</el-button>
          <el-button type="primary" :loading="passwordLoading" @click="handleChangePassword">确 定</el-button>
        </div>
      </el-dialog>
      </el-dropdown>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { resetPassword } from '@/api/auth'

export default {
  name: 'Navbar',
  data() {
    const validateConfirm = (rule, value, callback) => {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    return {
      levelList: [],
      passwordDialogVisible: false,
      passwordLoading: false,
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {
        oldPassword: [
          { required: true, message: '请输入当前密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: validateConfirm, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapState('app', ['sidebarCollapsed']),
    ...mapState('user', ['userInfo']),
    displayName() {
      if (!this.userInfo) return '未登录'
      const name = this.userInfo.username || this.userInfo.realName || '用户'
      const roleNames = this.userInfo.roleNames
      if (roleNames && roleNames.length > 0) {
        return `${name}（${roleNames.join('、')}）`
      }
      return name
    }
  },
  watch: {
    $route() {
      this.getBreadcrumb()
    }
  },
  created() {
    this.getBreadcrumb()
  },
  methods: {
    ...mapActions('app', ['toggleSidebar']),
    ...mapActions('user', ['logout']),

    // 获取面包屑
    getBreadcrumb() {
      const matched = this.$route.matched.filter(
        item => item.meta && item.meta.title
      )
      this.levelList = matched
    },

    // 处理下拉命令
    handleCommand(command) {
      switch (command) {
        case 'changePassword':
          this.passwordForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
          this.passwordDialogVisible = true
          this.$nextTick(() => {
            this.$refs.passwordForm && this.$refs.passwordForm.clearValidate()
          })
          break
        case 'logout':
          this.handleLogout()
          break
      }
    },

    // 修改密码
    handleChangePassword() {
      this.$refs.passwordForm.validate(valid => {
        if (!valid) return
        this.passwordLoading = true
        resetPassword({
          oldPassword: this.passwordForm.oldPassword,
          newPassword: this.passwordForm.newPassword
        }).then(() => {
          this.$message.success('密码修改成功，请重新登录')
          this.passwordDialogVisible = false
          // 修改密码后退出登录，让用户用新密码重新登录
          setTimeout(() => {
            this.logout().then(() => {
              this.$router.push('/login')
            })
          }, 1000)
        }).catch(() => {
          // 错误已由拦截器统一处理
        }).finally(() => {
          this.passwordLoading = false
        })
      })
    },

    // 退出登录
    handleLogout() {
      this.$confirm('确定要退出登录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.logout().then(() => {
          this.$router.push('/login')
        })
      }).catch(() => {})
    }
  }
}
</script>

<style lang="less" scoped>
.navbar-content {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 @spacing-medium;
  width: 100%;
}

.hamburger {
  font-size: 20px;
  cursor: pointer;
  color: @text-primary;
  line-height: 50px;

  &:hover {
    color: @primary-color;
  }
}

.breadcrumb {
  margin-left: @spacing-medium;
  flex: 1;
}

.right-menu {
  display: flex;
  align-items: center;

  .el-dropdown-link {
    cursor: pointer;
    display: flex;
    align-items: center;
    color: @text-primary;

    .username {
      margin: 0 6px;
    }

    &:hover {
      color: @primary-color;
    }
  }
}
</style>
